import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiAlertCircle,
  FiArrowLeft,
  FiCheckCircle,
  FiFileText,
  FiLoader,
  FiPackage,
  FiRotateCcw,
  FiSave,
  FiSearch,
  FiUser,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { apiRequest } from "../../../services/api";
import { useAuth } from "../../../context/AuthContext";

export default function CreateSalesReturn() {
  const navigate = useNavigate();
  const { accessToken } = useAuth();

  const [invoices, setInvoices] = useState([]);
  const [salesInvoiceId, setSalesInvoiceId] = useState("");
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [lineState, setLineState] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

  const loadEligibleInvoices = useCallback(async () => {
    setIsLoading(true);
    setError("");

    try {
      const response = await apiRequest("/sales-returns/eligible-invoices", {
        accessToken,
      });

      setInvoices(Array.isArray(response) ? response : []);
    } catch (requestError) {
      setError(
        requestError.message || "Could not load eligible sales invoices.",
      );
    } finally {
      setIsLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    loadEligibleInvoices();
  }, [loadEligibleInvoices]);

  const selectedInvoice = useMemo(
    () => invoices.find((invoice) => invoice.id === salesInvoiceId) || null,
    [invoices, salesInvoiceId],
  );

  const invoiceItems = useMemo(
    () => (Array.isArray(selectedInvoice?.items) ? selectedInvoice.items : []),
    [selectedInvoice],
  );

  useEffect(() => {
    if (!selectedInvoice) {
      setLineState({});
      return;
    }

    setLineState(
      Object.fromEntries(
        invoiceItems.map((item) => [
          item.id,
          {
            quantityReturned: "",
            restockToInventory: true,
          },
        ]),
      ),
    );
  }, [selectedInvoice, invoiceItems]);

  const selectedLines = useMemo(
    () =>
      invoiceItems.filter(
        (item) => Number(lineState[item.id]?.quantityReturned || 0) > 0,
      ),
    [invoiceItems, lineState],
  );

  const estimatedCredit = useMemo(
    () =>
      selectedLines.reduce((total, item) => {
        const quantityReturned = Number(
          lineState[item.id]?.quantityReturned || 0,
        );

        const quantitySold = Number(item.quantity || 0);
        const lineAmount = Number(item.lineTotalAmount || 0);

        if (quantitySold <= 0) return total;

        return total + (lineAmount / quantitySold) * quantityReturned;
      }, 0),
    [selectedLines, lineState],
  );

  const updateLine = (itemId, field, value) => {
    setLineState((current) => ({
      ...current,
      [itemId]: {
        ...current[itemId],
        [field]: value,
      },
    }));
  };

  const getReturnableQuantity = (item) => {
    const value =
      item.returnableQuantity ??
      item.remainingReturnQuantity ??
      item.remainingQuantity ??
      item.quantity ??
      0;

    return Number(value);
  };

  const selectAllReturnable = () => {
    setLineState(
      Object.fromEntries(
        invoiceItems.map((item) => [
          item.id,
          {
            quantityReturned: String(getReturnableQuantity(item)),
            restockToInventory: true,
          },
        ]),
      ),
    );
  };

  const clearLines = () => {
    setLineState(
      Object.fromEntries(
        invoiceItems.map((item) => [
          item.id,
          {
            quantityReturned: "",
            restockToInventory: true,
          },
        ]),
      ),
    );
  };

  const createReturn = async (event) => {
    event.preventDefault();
    setError("");

    if (!salesInvoiceId) {
      setError("Please select an issued sales invoice.");
      return;
    }

    if (!reason.trim()) {
      setError("Please enter the reason for this return.");
      return;
    }

    if (!selectedLines.length) {
      setError("Enter a return quantity for at least one item.");
      return;
    }

    const invalidLine = selectedLines.find((item) => {
      const quantityReturned = Number(
        lineState[item.id]?.quantityReturned || 0,
      );

      return (
        !Number.isFinite(quantityReturned) ||
        quantityReturned <= 0 ||
        quantityReturned > getReturnableQuantity(item)
      );
    });

    if (invalidLine) {
      setError(
        `Enter a valid quantity for ${invalidLine.productNameSnapshot || invalidLine.skuSnapshot}.`,
      );
      return;
    }

    setIsSaving(true);

    try {
      const createdReturn = await apiRequest("/sales-returns", {
        method: "POST",
        accessToken,
        body: {
          salesInvoiceId,
          reason: reason.trim(),
          notes: notes.trim() || undefined,
          items: selectedLines.map((item) => ({
            salesInvoiceItemId: item.id,
            quantityReturned: Number(lineState[item.id].quantityReturned),
            restockToInventory: Boolean(lineState[item.id].restockToInventory),
          })),
        },
      });

      navigate(`/admindashboard/sales/returns/${createdReturn.id}`, {
        replace: true,
      });
    } catch (requestError) {
      setError(requestError.message || "Could not create the sales return.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
        <div>
          <button
            onClick={() => navigate("/admindashboard/sales/returns")}
            className="flex items-center gap-2 text-gray-500 hover:text-primary-700 mb-4"
          >
            <FiArrowLeft />
            Back to Sales Returns
          </button>

          <h1 className="text-4xl font-bold text-gray-900">
            Create Sales Return
          </h1>

          <p className="mt-2 text-gray-500">
            Create a draft return from an issued invoice. Stock and the credit
            note are applied only when the return is received.
          </p>
        </div>

        <button
          type="submit"
          form="sales-return-form"
          disabled={isSaving || !selectedInvoice || !selectedLines.length}
          className="h-11 px-6 rounded-xl bg-primary-700 text-white flex items-center gap-2 hover:bg-primary-800 disabled:opacity-60"
        >
          {isSaving ? <FiLoader className="animate-spin" /> : <FiSave />}
          Save Return Draft
        </button>
      </div>

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-red-700 flex gap-3">
          <FiAlertCircle className="mt-0.5 shrink-0" />
          <p>{error}</p>
        </div>
      )}

      <form
        id="sales-return-form"
        onSubmit={createReturn}
        className="space-y-6"
      >
        <div className="bg-white rounded-3xl border border-gray-100 p-8">
          <div className="flex items-center gap-3 mb-7">
            <div className="h-11 w-11 rounded-2xl bg-primary-50 text-primary-700 flex items-center justify-center">
              <FiFileText size={21} />
            </div>

            <div>
              <h2 className="text-2xl font-bold">Select Invoice</h2>
              <p className="text-sm text-gray-500 mt-1">
                Only issued invoices with returnable items are shown.
              </p>
            </div>
          </div>

          <div className="grid xl:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium mb-2">
                Issued Sales Invoice *
              </label>

              <div className="relative">
                <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />

                <select
                  value={salesInvoiceId}
                  onChange={(event) => setSalesInvoiceId(event.target.value)}
                  disabled={isLoading}
                  className="w-full h-12 rounded-xl border border-gray-200 pl-11 pr-4 bg-white focus:outline-none focus:border-primary-700 disabled:bg-gray-50"
                >
                  <option value="">
                    {isLoading
                      ? "Loading eligible invoices..."
                      : "Select an issued invoice"}
                  </option>

                  {invoices.map((invoice) => (
                    <option key={invoice.id} value={invoice.id}>
                      {invoice.invoiceCode} —{" "}
                      {invoice.customerNameSnapshot || "Walk-in Customer"}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Return Reason *
              </label>

              <input
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder="Example: Damaged item, wrong size, customer return"
                className="w-full h-12 rounded-xl border border-gray-200 px-4 focus:outline-none focus:border-primary-700"
              />
            </div>
          </div>

          {selectedInvoice && (
            <div className="mt-7 grid md:grid-cols-3 gap-5">
              <InfoCard
                icon={<FiFileText />}
                label="Invoice"
                value={selectedInvoice.invoiceCode}
                color="bg-blue-50 text-blue-700"
              />

              <InfoCard
                icon={<FiUser />}
                label="Customer"
                value={
                  selectedInvoice.customerNameSnapshot || "Walk-in Customer"
                }
                color="bg-green-50 text-green-700"
              />

              <InfoCard
                icon={<FiPackage />}
                label="Store"
                value={selectedInvoice.store?.name || "—"}
                color="bg-purple-50 text-purple-700"
              />
            </div>
          )}
        </div>

        {selectedInvoice && (
          <>
            <div className="bg-white rounded-3xl border border-gray-100 overflow-hidden">
              <div className="p-7 border-b border-gray-100 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <h2 className="text-2xl font-bold">Return Items</h2>
                  <p className="text-sm text-gray-500 mt-1">
                    Enter quantities actually returned. Turn off restocking for
                    damaged or non-sellable items.
                  </p>
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={clearLines}
                    className="h-10 px-4 rounded-xl border border-gray-200 hover:bg-gray-50"
                  >
                    Clear
                  </button>

                  <button
                    type="button"
                    onClick={selectAllReturnable}
                    className="h-10 px-4 rounded-xl bg-primary-50 text-primary-700 font-medium hover:bg-primary-100"
                  >
                    Return All
                  </button>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full min-w-[1100px]">
                  <thead className="bg-gray-50">
                    <tr>
                      <TableHeader>Product</TableHeader>
                      <TableHeader>SKU</TableHeader>
                      <TableHeader center>Invoiced Qty</TableHeader>
                      <TableHeader center>Returnable Qty</TableHeader>
                      <TableHeader right>Line Amount</TableHeader>
                      <TableHeader center>Return Qty</TableHeader>
                      <TableHeader center>Restock</TableHeader>
                      <TableHeader right>Estimated Credit</TableHeader>
                    </tr>
                  </thead>

                  <tbody>
                    {invoiceItems.map((item) => {
                      const quantitySold = Number(item.quantity || 0);
                      const returnableQuantity = getReturnableQuantity(item);
                      const quantityReturned = Number(
                        lineState[item.id]?.quantityReturned || 0,
                      );
                      const lineAmount = Number(item.lineTotalAmount || 0);

                      const estimatedLineCredit =
                        quantitySold > 0
                          ? (lineAmount / quantitySold) * quantityReturned
                          : 0;

                      return (
                        <tr
                          key={item.id}
                          className="border-b border-gray-100 last:border-b-0"
                        >
                          <td className="px-5 py-5">
                            <h3 className="font-semibold">
                              {item.productNameSnapshot}
                            </h3>

                            <p className="text-sm text-gray-500 mt-1">
                              {item.variantNameSnapshot || "Default variant"}
                            </p>
                          </td>

                          <td className="px-5 py-5 text-sm text-gray-600">
                            {item.skuSnapshot || "—"}
                          </td>

                          <td className="px-5 py-5 text-center">
                            {quantitySold}
                          </td>

                          <td className="px-5 py-5 text-center font-semibold text-primary-700">
                            {returnableQuantity}
                          </td>

                          <td className="px-5 py-5 text-right">
                            {formatMoney(lineAmount)}
                          </td>

                          <td className="px-5 py-5 text-center">
                            <input
                              type="number"
                              min="0"
                              max={returnableQuantity}
                              step="0.001"
                              value={lineState[item.id]?.quantityReturned || ""}
                              onChange={(event) =>
                                updateLine(
                                  item.id,
                                  "quantityReturned",
                                  event.target.value,
                                )
                              }
                              className="h-10 w-28 rounded-xl border border-gray-200 px-3 text-center focus:outline-none focus:border-primary-700"
                            />
                          </td>

                          <td className="px-5 py-5 text-center">
                            <label className="inline-flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={
                                  lineState[item.id]?.restockToInventory ?? true
                                }
                                onChange={(event) =>
                                  updateLine(
                                    item.id,
                                    "restockToInventory",
                                    event.target.checked,
                                  )
                                }
                                className="h-4 w-4 accent-primary-700"
                              />
                              <span className="text-sm text-gray-600">
                                Restock
                              </span>
                            </label>
                          </td>

                          <td className="px-5 py-5 text-right font-semibold text-green-700">
                            {formatMoney(estimatedLineCredit)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="grid xl:grid-cols-3 gap-6">
              <div className="xl:col-span-2 bg-white rounded-3xl border border-gray-100 p-8">
                <h2 className="text-2xl font-bold mb-6">Internal Notes</h2>

                <textarea
                  rows={6}
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                  placeholder="Add return condition, inspection notes, or other internal details..."
                  className="w-full rounded-2xl border border-gray-200 p-4 resize-none focus:outline-none focus:border-primary-700"
                />
              </div>

              <div className="bg-primary-700 rounded-3xl p-7 text-white">
                <div className="h-12 w-12 rounded-2xl bg-white/15 flex items-center justify-center">
                  <FiRotateCcw size={23} />
                </div>

                <p className="mt-6 text-white/70 text-sm">
                  Selected Return Items
                </p>

                <h2 className="mt-2 text-4xl font-bold">
                  {selectedLines.length}
                </h2>

                <div className="mt-7 pt-6 border-t border-white/20 flex justify-between gap-4">
                  <span className="text-white/80">Estimated Credit</span>
                  <span className="font-bold text-lg">
                    {formatMoney(estimatedCredit)}
                  </span>
                </div>

                <p className="mt-6 text-sm leading-relaxed text-white/75">
                  A credit note and inventory movement are created only after
                  the returned items are received.
                </p>
              </div>
            </div>
          </>
        )}

        {!isLoading && !invoices.length && (
          <div className="bg-white rounded-3xl border border-gray-100 p-12 text-center">
            <FiFileText size={36} className="mx-auto text-gray-300 mb-4" />

            <h2 className="text-xl font-bold">No eligible invoices found</h2>

            <p className="text-gray-500 mt-2">
              An invoice must be issued and contain returnable items before a
              sales return can be created.
            </p>
          </div>
        )}
      </form>
    </div>
  );
}

function InfoCard({ icon, label, value, color }) {
  return (
    <div className="bg-gray-50 rounded-2xl p-5 flex items-center gap-4">
      <div
        className={`h-11 w-11 rounded-xl flex items-center justify-center ${color}`}
      >
        {icon}
      </div>

      <div className="min-w-0">
        <p className="text-sm text-gray-500">{label}</p>
        <p className="font-semibold text-gray-900 mt-1 truncate">{value}</p>
      </div>
    </div>
  );
}

function TableHeader({ children, right = false, center = false }) {
  return (
    <th
      className={`px-5 py-4 text-xs font-semibold uppercase tracking-wider text-gray-500 ${
        right ? "text-right" : center ? "text-center" : "text-left"
      }`}
    >
      {children}
    </th>
  );
}

function formatMoney(value) {
  return `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}
